import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  Alert,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  AssemblyTemplateField,
  PointOfInterestTemplateField,
  AllTemplatesForBuilderQuery,
  useUpdateTemplateMutation,
  useUpdatePointOfInterestTemplateMutation,
  AllTemplatesForBuilderDocument,
  TemplateTypeEnum,
  PointOfInterestTemplateTypeEnum,
  AllPointOfInterestTemplateQuery,
} from '@/__generated__/graphql';
import { abyssColors } from '@/theme/colors';
import { ConditionBuilder } from './ConditionBuilder';
import { EquationBuilder } from './EquationBuilder';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import CircularProgress from '@mui/material/CircularProgress';
import { useAtomValue } from 'jotai';
import { allPOITypes } from '../state';

interface FieldLogicConfiguratorProps {
  field: AssemblyTemplateField | PointOfInterestTemplateField;
  template:
    | AllTemplatesForBuilderQuery['allTemplates'][0]
    | AllPointOfInterestTemplateQuery['allPointOfInterestTemplate'][0];
  templateType: 'assembly' | 'poi';
  structureId?: string;
  onUpdate: (field: AssemblyTemplateField | PointOfInterestTemplateField) => void;
}

export const FieldLogicConfigurator = ({
  field,
  template,
  templateType,
  structureId,
  onUpdate,
}: FieldLogicConfiguratorProps) => {
  const [localField, setLocalField] = useState<
    AssemblyTemplateField | PointOfInterestTemplateField
  >(field);
  const [hasChanges, setHasChanges] = useState(false);
  const [logicRuleTab, setLogicRuleTab] = useState(0);
  const [calculationTab, setCalculationTab] = useState(0);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const poiTemplates = useAtomValue(allPOITypes);

  // Get available equations from template (only Assembly templates have equations)
  const availableEquations = useMemo(() => {
    return 'equations' in template ? template.equations || [] : [];
  }, [template]);

  // Get available fields for variables
  const availableFields = useMemo(() => {
    return template.fields || [];
  }, [template.fields]);

  // Determine if this is a POI template
  const isPOITemplate = templateType === 'poi';

  // Mutation for updating assembly template
  const [updateTemplate, { loading: isAssemblySaving }] = useUpdateTemplateMutation({
    refetchQueries: [AllTemplatesForBuilderDocument],
    onCompleted: (response) => {
      if (!response?.updateTemplate) {
        showSnackBar('An error occurred while saving logic rules', 'error');
        return;
      }
      showSnackBar('Logic rules saved successfully', 'success');
      setHasChanges(false);

      // Update the field with the response data
      const updatedField = response.updateTemplate.fields?.find((f) => f.id === localField.id);
      if (updatedField) {
        onUpdate(updatedField as AssemblyTemplateField | PointOfInterestTemplateField);
      }
    },
    onError: (error) => {
      console.error('Error saving logic rules:', error);
      showSnackBar(error.message || 'Failed to save logic rules', 'error');
    },
  });

  // Mutation for updating POI template
  const [updatePOITemplate, { loading: isPOISaving }] = useUpdatePointOfInterestTemplateMutation({
    refetchQueries: [AllTemplatesForBuilderDocument],
    onCompleted: (response) => {
      if (!response?.updatePointOfInterestTemplate) {
        showSnackBar('An error occurred while saving logic rules', 'error');
        return;
      }
      showSnackBar('Logic rules saved successfully', 'success');
      setHasChanges(false);

      // Update the field with the response data
      const updatedField = response.updatePointOfInterestTemplate.fields?.find(
        (f) => f.id === localField.id
      );
      if (updatedField) {
        onUpdate(updatedField as AssemblyTemplateField | PointOfInterestTemplateField);
      }
    },
    onError: (error) => {
      console.error('Error saving logic rules:', error);
      showSnackBar(error.message || 'Failed to save logic rules', 'error');
    },
  });

  const isSaving = isPOITemplate ? isPOISaving : isAssemblySaving;

  // Sync local state when field prop changes
  useEffect(() => {
    setLocalField(field);
    setHasChanges(false);
  }, [field]);

  const handleToggle = (flag: keyof AssemblyTemplateField | keyof PointOfInterestTemplateField) => {
    const updated = { ...localField, [flag]: !(localField as any)[flag] };  
    setLocalField(updated);
    setHasChanges(true);
  };

  const handleSave = useCallback(() => {
    // Recursively clean conditionTree nodes
     
    const cleanConditionTree = (node: any): any => {
      if (!node) return node;

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { __typename, id, ...cleanedNode } = node;

      // Remove null enum values - they should be undefined or omitted
      if (cleanedNode.operator === null) delete cleanedNode.operator;
      if (cleanedNode.logicOperator === null) delete cleanedNode.logicOperator;
      if (cleanedNode.inputVariable === null) delete cleanedNode.inputVariable;
      if (cleanedNode.value === null) delete cleanedNode.value;

      // Recursively clean nested conditions
      if (cleanedNode.conditions && Array.isArray(cleanedNode.conditions)) {
        cleanedNode.conditions = cleanedNode.conditions.map(cleanConditionTree);
      }

      return cleanedNode;
    };

    // Prepare all template fields, removing __typename
     
    const cleanField = (field: any) => {
      const cleaned = { ...field };
      delete cleaned.__typename;

      // Clean displayConfig
      if (cleaned.displayConfig?.__typename) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { __typename, ...rest } = cleaned.displayConfig;
        if (rest.color) {
           
          rest.color = rest.color.map((color: any) =>
            JSON.parse(JSON.stringify(color), (key, value) => {
              return key === '__typename' ? undefined : value;
            })
          );
        }
        cleaned.displayConfig = rest;
      }

      // Clean optionsData
      if (cleaned.optionsData) {
         
        cleaned.optionsData = cleaned.optionsData.map((option: any) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { __typename, ...rest } = option;
          return {
            ...rest,
            weight: option.weight ?? 0,
          };
        });
      }

      // Clean logicRule
      if (cleaned.logicRule) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { __typename, ...logicRuleRest } = cleaned.logicRule;

        // Set default evaluationStrategy if null or undefined (required field)
        if (!logicRuleRest.evaluationStrategy) {
          logicRuleRest.evaluationStrategy = 'FIRST_MATCH';
        }
        // Remove null values from optional fields
        if (logicRuleRest.defaultValue === null) delete logicRuleRest.defaultValue;
        if (logicRuleRest.description === null) delete logicRuleRest.description;

        cleaned.logicRule = {
          ...logicRuleRest,
           
          variables: logicRuleRest.variables?.map((v: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { __typename, ...varRest } = v;
            // Remove null values from variables
            if (varRest.templateType === null) delete varRest.templateType;
            if (varRest.isBlisterField === null) delete varRest.isBlisterField;
            return varRest;
          }),
           
          conditionalMappings: logicRuleRest.conditionalMappings?.map((m: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { __typename, id, ...mappingRest } = m;
            // Remove null enum values
            if (mappingRest.combineOperator === null) delete mappingRest.combineOperator;
            return {
              ...mappingRest,
              conditionTree: cleanConditionTree(mappingRest.conditionTree),
            };
          }),
        };
      }

      // Clean calculatedBy
      if (cleaned.calculatedBy) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { __typename, ...calculatedByRest } = cleaned.calculatedBy;

        // Set default evaluationStrategy if null or undefined (required field)
        if (!calculatedByRest.evaluationStrategy) {
          calculatedByRest.evaluationStrategy = 'FIRST_MATCH';
        }
        // Remove null values from optional fields
        if (calculatedByRest.defaultValue === null) delete calculatedByRest.defaultValue;
        if (calculatedByRest.description === null) delete calculatedByRest.description;

        cleaned.calculatedBy = {
          ...calculatedByRest,
           
          variables: calculatedByRest.variables?.map((v: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { __typename, ...varRest } = v;
            // Remove null values from variables
            if (varRest.templateType === null) delete varRest.templateType;
            if (varRest.isBlisterField === null) delete varRest.isBlisterField;
            return varRest;
          }),
           
          conditionalMappings: calculatedByRest.conditionalMappings?.map((m: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { __typename, id, ...mappingRest } = m;
            // Remove null enum values
            if (mappingRest.combineOperator === null) delete mappingRest.combineOperator;
            return {
              ...mappingRest,
              conditionTree: cleanConditionTree(mappingRest.conditionTree),
            };
          }),
        };
      }

      return cleaned;
    };

    // Get all fields from template and update the current field
    const allFields =
      template.fields?.map((f) => {
        if (f.id === localField.id) {
          return cleanField(localField);
        }
        return cleanField(f);
      }) || [];

    // Use the appropriate mutation based on template type
    if (isPOITemplate) {
      updatePOITemplate({
        variables: {
          input: {
            pointOfInterestTemplateId: template.id,
            name: template.name || 'POI Template',
            fields: allFields as any,  
            lastModifiedFor: 'Update Logic Rules',
            type:
              'type' in template && template.type
                ? (template.type as unknown as PointOfInterestTemplateTypeEnum)
                : PointOfInterestTemplateTypeEnum.PointOfInterest,
          },
        },
      });
    } else {
      updateTemplate({
        variables: {
          input: {
            templateId: template.id,
            type: TemplateTypeEnum.Assembly,
            structureId: structureId || '',
            name: template.name || 'Assembly Template',
            fields: allFields,
            lastModifiedFor: 'Update Logic Rules',
          },
        },
      });
    }
  }, [localField, template, structureId, isPOITemplate, updateTemplate, updatePOITemplate]);

   
  const handleUpdateLogicRule = (conditionalMappings: any[]) => {
    const updated = {
      ...localField,
      logicRule: {
        ...localField.logicRule,
        conditionalMappings,
      } as any,  
    };
    setLocalField(updated);
    setHasChanges(true);
  };

   
  const handleUpdateLogicRuleVariables = (variables: any[]) => {
    const updated = {
      ...localField,
      logicRule: {
        ...localField.logicRule,
        variables,
      } as any,  
    };
    setLocalField(updated);
    setHasChanges(true);
  };

   
  const handleUpdateLogicRuleEvaluationStrategy = (evaluationStrategy: any) => {
    const updated = {
      ...localField,
      logicRule: {
        ...localField.logicRule,
        evaluationStrategy,
      } as any,  
    };
    setLocalField(updated);
    setHasChanges(true);
  };

   
  const handleUpdateCalculation = (conditionalMappings: any[]) => {
    const updated = {
      ...localField,
      calculatedBy: {
        ...localField.calculatedBy,
        conditionalMappings,
      } as any,  
    };
    setLocalField(updated);
    setHasChanges(true);
  };

   
  const handleUpdateCalculationVariables = (variables: any[]) => {
    const updated = {
      ...localField,
      calculatedBy: {
        ...localField.calculatedBy,
        variables,
      } as any,  
    };
    setLocalField(updated);
    setHasChanges(true);
  };

   
  const handleUpdateCalculationEvaluationStrategy = (evaluationStrategy: any) => {
    const updated = {
      ...localField,
      calculatedBy: {
        ...localField.calculatedBy,
        evaluationStrategy,
      } as any,  
    };
    setLocalField(updated);
    setHasChanges(true);
  };

  // Helper function to render condition tree in readable format
   
  const renderConditionTree = (conditionTree: any, depth: number = 0): JSX.Element => {
    if (!conditionTree) return <></>;

    const indent = depth * 2;

    if (conditionTree.nodeType === 'SIMPLE') {
      return (
        <Box sx={{ pl: indent }}>
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'monospace',
              color: abyssColors.neutral[700],
              display: 'block',
            }}
          >
            <Box component="span" sx={{ color: abyssColors.primary[600], fontWeight: 600 }}>
              {conditionTree.inputVariable}
            </Box>{' '}
            <Box component="span" sx={{ color: abyssColors.tertiary[600] }}>
              {conditionTree.operator}
            </Box>{' '}
            <Box component="span" sx={{ color: abyssColors.success[600] }}>
              "{conditionTree.value}"
            </Box>
          </Typography>
        </Box>
      );
    }

    if (conditionTree.nodeType === 'GROUP' && conditionTree.conditions) {
      return (
        <Box sx={{ pl: indent }}>
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'monospace',
              fontWeight: 600,
              color: abyssColors.warning[700],
              display: 'block',
              mb: 0.5,
            }}
          >
            {conditionTree.logicOperator || 'AND'}:
          </Typography>
          <Stack spacing={0.5}>
            { }
            {conditionTree.conditions.map((condition: any, index: number) => (
              <Box key={index}>{renderConditionTree(condition, depth + 1)}</Box>
            ))}
          </Stack>
        </Box>
      );
    }

    return (
      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
        {conditionTree.nodeType || 'Unknown'}
      </Typography>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack>
            <Typography variant="h6" fontWeight={600}>
              {field.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {field.key}
            </Typography>
          </Stack>
          {hasChanges && (
            <Button
              variant="contained"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={isSaving}
              sx={{
                backgroundColor: abyssColors.primary[500],
                '&:hover': { backgroundColor: abyssColors.primary[600] },
              }}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </Stack>

        <Divider />

        {/* Evaluation Flags */}
        <Card elevation={0}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Evaluation Configuration
            </Typography>

            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localField.shouldEvaluateLogic || false}
                    onChange={() => handleToggle('shouldEvaluateLogic')}
                    color="success"
                  />
                }
                label={
                  <Stack>
                    <Typography variant="body2" fontWeight={500}>
                      Enable Logic Evaluation
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Evaluate field value using conditional logic rules
                    </Typography>
                  </Stack>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={localField.shouldEvaluateMath || false}
                    onChange={() => handleToggle('shouldEvaluateMath')}
                    color="primary"
                  />
                }
                label={
                  <Stack>
                    <Typography variant="body2" fontWeight={500}>
                      Enable Mathematical Calculation
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Calculate field value using mathematical expressions
                    </Typography>
                  </Stack>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={localField.shouldEvaluateLogicPostCalculation || false}
                    onChange={() => handleToggle('shouldEvaluateLogicPostCalculation')}
                    color="warning"
                  />
                }
                label={
                  <Stack>
                    <Typography variant="body2" fontWeight={500}>
                      Enable Post-Calculation Logic
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Apply logic rules after mathematical calculations
                    </Typography>
                  </Stack>
                }
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Logic Rule Configuration */}
        {(localField.shouldEvaluateLogic || localField.logicRule) && (
          <Accordion defaultExpanded elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Logic Rule Configuration
                </Typography>
                <Chip
                  label="Logic"
                  size="small"
                  sx={{
                    backgroundColor: abyssColors.success[50],
                    color: abyssColors.success[700],
                    height: '20px',
                  }}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {/* Tabs for View/Edit */}
                <Tabs
                  value={logicRuleTab}
                  onChange={(_, newValue) => setLogicRuleTab(newValue)}
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab icon={<VisibilityIcon />} label="View" iconPosition="start" />
                  <Tab icon={<EditIcon />} label="Edit Conditions" iconPosition="start" />
                </Tabs>

                {logicRuleTab === 0 ? (
                  // View Tab
                  localField.logicRule ? (
                    <>
                      <Alert severity="info" icon={<InfoOutlinedIcon />}>
                        Logic rules are configured with conditional mappings and evaluation
                        strategies.
                      </Alert>

                      {/* Description */}
                      <TextField
                        label="Description"
                        value={localField.logicRule.description || ''}
                        multiline
                        rows={2}
                        fullWidth
                        disabled
                        helperText="Rule description"
                      />

                      {/* Evaluation Strategy */}
                      <FormControl fullWidth disabled>
                        <InputLabel>Evaluation Strategy</InputLabel>
                        <Select
                          value={localField.logicRule.evaluationStrategy || ''}
                          label="Evaluation Strategy"
                        >
                          <MenuItem value="CONDITIONAL">Conditional</MenuItem>
                          <MenuItem value="SEQUENTIAL">Sequential</MenuItem>
                          <MenuItem value="PRIORITY">Priority</MenuItem>
                        </Select>
                      </FormControl>

                      {/* Default Value */}
                      <TextField
                        label="Default Value"
                        value={localField.logicRule.defaultValue || ''}
                        fullWidth
                        disabled
                        helperText="Value used when no conditions match"
                      />

                      {/* Variables */}
                      {localField.logicRule.variables &&
                        localField.logicRule.variables.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                              Variables ({localField.logicRule.variables.length})
                            </Typography>
                            <Stack spacing={1}>
                              {localField.logicRule.variables.map((variable, index) => (
                                <Card
                                  key={index}
                                  elevation={0}
                                  sx={{ backgroundColor: abyssColors.neutral[50], border: 'none' }}
                                  variant="outlined"
                                >
                                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        sx={{ fontFamily: 'monospace' }}
                                      >
                                        {variable.name}
                                      </Typography>
                                      <Chip
                                        label={variable.fieldKey}
                                        size="small"
                                        sx={{ fontSize: '0.875rem' }}
                                      />
                                      {variable.templateType && (
                                        <Chip
                                          label={variable.templateType}
                                          size="small"
                                          sx={{ fontSize: '0.875rem' }}
                                        />
                                      )}
                                    </Stack>
                                  </CardContent>
                                </Card>
                              ))}
                            </Stack>
                          </Box>
                        )}

                      {/* Conditional Mappings */}
                      {localField.logicRule.conditionalMappings &&
                        localField.logicRule.conditionalMappings.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                              Conditional Mappings (
                              {localField.logicRule.conditionalMappings.length})
                            </Typography>
                            <Stack spacing={1}>
                              {localField.logicRule.conditionalMappings.map((mapping, index) => (
                                <Card
                                  key={index}
                                  elevation={0}
                                  sx={{ backgroundColor: abyssColors.neutral[50] }}
                                  variant="outlined"
                                >
                                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Stack spacing={1.5}>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          fontWeight={600}
                                        >
                                          THEN:
                                        </Typography>
                                        <Chip
                                          label={mapping.value}
                                          size="small"
                                          color="primary"
                                          sx={{ fontSize: '0.875rem' }}
                                        />
                                      </Stack>
                                      {mapping.conditionTree && (
                                        <Box>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight={600}
                                            sx={{ display: 'block', mb: 0.5 }}
                                          >
                                            IF:
                                          </Typography>
                                          <Box
                                            sx={{
                                              backgroundColor: abyssColors.neutral[100],
                                              p: 1,
                                              borderRadius: 1,
                                              border: `1px solid ${abyssColors.neutral[200]}`,
                                            }}
                                          >
                                            {renderConditionTree(mapping.conditionTree)}
                                          </Box>
                                        </Box>
                                      )}
                                    </Stack>
                                  </CardContent>
                                </Card>
                              ))}
                            </Stack>
                          </Box>
                        )}
                    </>
                  ) : (
                    <Alert severity="warning">
                      No logic rule configured. Enable logic evaluation to create rules.
                    </Alert>
                  )
                ) : (
                  // Edit Tab
                  <Box>
                    <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
                      Build conditional logic rules using IF-THEN mappings. Define conditions that
                      determine the field value.
                    </Alert>
                    <ConditionBuilder
                      availableFields={availableFields as any}  
                      conditionalMappings={(localField.logicRule?.conditionalMappings as any) || []}  
                      onUpdate={handleUpdateLogicRule}
                      variables={(localField.logicRule?.variables as any) || []}  
                      onVariablesUpdate={handleUpdateLogicRuleVariables}
                      evaluationStrategy={
                        (localField.logicRule?.evaluationStrategy as any) || undefined  
                      }
                      onEvaluationStrategyUpdate={handleUpdateLogicRuleEvaluationStrategy}
                      poiTemplates={poiTemplates || []}
                    />
                  </Box>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Calculation Configuration */}
        {(localField.shouldEvaluateMath || localField.calculatedBy) && (
          <Accordion elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Calculation Configuration
                </Typography>
                <Chip
                  label="Math"
                  size="small"
                  sx={{
                    backgroundColor: abyssColors.tertiary[50],
                    color: abyssColors.tertiary[700],
                    height: '20px',
                  }}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {/* Tabs for View/Edit */}
                <Tabs
                  value={calculationTab}
                  onChange={(_, newValue) => setCalculationTab(newValue)}
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab icon={<VisibilityIcon />} label="View" iconPosition="start" />
                  <Tab icon={<EditIcon />} label="Edit Calculations" iconPosition="start" />
                </Tabs>

                {calculationTab === 0 ? (
                  // View Tab
                  localField.calculatedBy ? (
                    <>
                      <Alert severity="info" icon={<InfoOutlinedIcon />}>
                        Calculations use mathematical expressions and equations to compute field
                        values.
                      </Alert>

                      {/* Description */}
                      <TextField
                        label="Description"
                        value={localField.calculatedBy.description || ''}
                        multiline
                        rows={2}
                        fullWidth
                        disabled
                        helperText="Calculation description"
                      />

                      {/* Evaluation Strategy */}
                      <FormControl fullWidth disabled>
                        <InputLabel>Evaluation Strategy</InputLabel>
                        <Select
                          value={localField.calculatedBy.evaluationStrategy || ''}
                          label="Evaluation Strategy"
                        >
                          <MenuItem value="EQUATION">Equation</MenuItem>
                          <MenuItem value="CONDITIONAL">Conditional</MenuItem>
                          <MenuItem value="DIRECT">Direct</MenuItem>
                        </Select>
                      </FormControl>

                      {/* Default Value */}
                      <TextField
                        label="Default Value"
                        value={localField.calculatedBy.defaultValue || ''}
                        fullWidth
                        disabled
                        helperText="Value used when calculation fails"
                      />

                      {/* Variables */}
                      {localField.calculatedBy.variables &&
                        localField.calculatedBy.variables.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                              Variables ({localField.calculatedBy.variables.length})
                            </Typography>
                            <Stack spacing={1}>
                              {localField.calculatedBy.variables.map((variable, index) => (
                                <Card
                                  key={index}
                                  elevation={0}
                                  sx={{ backgroundColor: abyssColors.neutral[50], border: 'none' }}
                                  variant="outlined"
                                >
                                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        sx={{ fontFamily: 'monospace' }}
                                      >
                                        {variable.name}
                                      </Typography>
                                      <Chip
                                        label={variable.fieldKey}
                                        size="small"
                                        sx={{ fontSize: '0.875rem' }}
                                      />
                                      {variable.templateType && (
                                        <Chip
                                          label={variable.templateType}
                                          size="small"
                                          sx={{ fontSize: '0.875rem' }}
                                        />
                                      )}
                                    </Stack>
                                  </CardContent>
                                </Card>
                              ))}
                            </Stack>
                          </Box>
                        )}

                      {/* Conditional Mappings for Calculations */}
                      {localField.calculatedBy.conditionalMappings &&
                        localField.calculatedBy.conditionalMappings.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                              Conditional Calculations (
                              {localField.calculatedBy.conditionalMappings.length})
                            </Typography>
                            <Stack spacing={1}>
                              {localField.calculatedBy.conditionalMappings.map((mapping, index) => (
                                <Card
                                  key={index}
                                  elevation={0}
                                  sx={{ backgroundColor: abyssColors.neutral[50] }}
                                  variant="outlined"
                                >
                                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Stack spacing={1.5}>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          fontWeight={600}
                                        >
                                          THEN:
                                        </Typography>
                                        <Chip
                                          label={mapping.value}
                                          size="small"
                                          color="primary"
                                          sx={{ fontSize: '0.875rem' }}
                                        />
                                      </Stack>
                                      {mapping.conditionTree && (
                                        <Box>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight={600}
                                            sx={{ display: 'block', mb: 0.5 }}
                                          >
                                            IF:
                                          </Typography>
                                          <Box
                                            sx={{
                                              backgroundColor: abyssColors.neutral[100],
                                              p: 1,
                                              borderRadius: 1,
                                              border: `1px solid ${abyssColors.neutral[200]}`,
                                            }}
                                          >
                                            {renderConditionTree(mapping.conditionTree)}
                                          </Box>
                                        </Box>
                                      )}
                                    </Stack>
                                  </CardContent>
                                </Card>
                              ))}
                            </Stack>
                          </Box>
                        )}
                    </>
                  ) : (
                    <Alert severity="warning">
                      No calculation configured. Enable mathematical calculation to create rules.
                    </Alert>
                  )
                ) : (
                  // Edit Tab
                  <Box>
                    <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
                      Build conditional calculations using IF-THEN mappings. Define conditions that
                      determine which calculation to use.
                    </Alert>
                    <ConditionBuilder
                      availableFields={availableFields as any}  
                      conditionalMappings={
                        (localField.calculatedBy?.conditionalMappings as any) || []  
                      }
                      onUpdate={handleUpdateCalculation}
                      variables={(localField.calculatedBy?.variables as any) || []}  
                      onVariablesUpdate={handleUpdateCalculationVariables}
                      evaluationStrategy={
                        (localField.calculatedBy?.evaluationStrategy as any) || undefined  
                      }
                      onEvaluationStrategyUpdate={handleUpdateCalculationEvaluationStrategy}
                      poiTemplates={poiTemplates || []}
                    />
                  </Box>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Manage Equations */}
        <Accordion elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                Manage Template Equations
              </Typography>
              <Chip
                label={`${availableEquations.length} equations`}
                size="small"
                sx={{
                  backgroundColor: abyssColors.tertiary[50],
                  color: abyssColors.tertiary[700],
                }}
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
              Equations are mathematical expressions that can be referenced in calculations. They
              are shared across all fields in the template.
            </Alert>
            <EquationBuilder
              availableFields={availableFields as any}  
              equations={availableEquations as any}  
              onUpdate={(updatedEquations) => {
                // Update template equations
                // This would typically trigger a mutation to save to backend
                // eslint-disable-next-line no-console
                console.log('Updated equations:', updatedEquations);
                setHasChanges(true);
              }}
              evaluationStrategy={(localField.calculatedBy?.evaluationStrategy as any) || undefined}  
              onEvaluationStrategyUpdate={handleUpdateCalculationEvaluationStrategy}
              poiTemplates={poiTemplates || []}
            />
          </AccordionDetails>
        </Accordion>

        {/* Help Section */}
        <Card
          elevation={0}
          sx={{ backgroundColor: abyssColors.primary[50], border: 'none' }}
          variant="outlined"
        >
          <CardContent>
            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight={600} color={abyssColors.primary[700]}>
                Configuration Guide
              </Typography>
              <Typography variant="caption" color="text.secondary">
                • <strong>Logic Evaluation:</strong> Use conditional rules to determine field values
                based on other field values
              </Typography>
              <Typography variant="caption" color="text.secondary">
                • <strong>Mathematical Calculation:</strong> Use equations and expressions to
                calculate numeric values
              </Typography>
              <Typography variant="caption" color="text.secondary">
                • <strong>Post-Calculation Logic:</strong> Apply logic rules after calculations are
                complete
              </Typography>
              <Typography variant="caption" color="text.secondary">
                • <strong>Variables:</strong> Reference other template fields in your rules and
                calculations
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};
